/**
 * Image Compression Utility
 * 
 * This utility provides functions to compress images before uploading to reduce storage costs
 * and improve upload performance. It maintains good quality while reducing file size.
 */

/**
 * Compress an image file
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    maxSizeKB = 500, // Target max size in KB
    format = 'image/jpeg' // Output format
  } = options;

  // Return original file if it's not an image
  if (!file.type.startsWith('image/')) {
    return file;
  }

  // Return original file if it's already small enough
  if (file.size <= maxSizeKB * 1024) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height;
        
        if (width > height) {
          width = Math.min(width, maxWidth);
          height = width / aspectRatio;
        } else {
          height = Math.min(height, maxHeight);
          width = height * aspectRatio;
        }
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw and compress image
      ctx.drawImage(img, 0, 0, width, height);

      // Try different quality levels to achieve target size
      let currentQuality = quality;
      let attempts = 0;
      const maxAttempts = 5;

      const tryCompress = () => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          // If size is acceptable or we've tried enough times, use this result
          if (blob.size <= maxSizeKB * 1024 || attempts >= maxAttempts) {
            const compressedFile = new File([blob], file.name, {
              type: format,
              lastModified: Date.now()
            });
            
            console.log(`Image compressed: ${(file.size / 1024).toFixed(1)}KB → ${(blob.size / 1024).toFixed(1)}KB (${((1 - blob.size / file.size) * 100).toFixed(1)}% reduction)`);
            resolve(compressedFile);
          } else {
            // Try with lower quality
            attempts++;
            currentQuality *= 0.8;
            tryCompress();
          }
        }, format, currentQuality);
      };

      tryCompress();
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for compression'));
    };

    // Load the image
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Compress multiple images
 * @param {File[]} files - Array of image files to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File[]>} - Array of compressed image files
 */
export const compressImages = async (files, options = {}) => {
  const compressionPromises = files.map(file => compressImage(file, options));
  return Promise.all(compressionPromises);
};

/**
 * Get optimal compression settings based on file type and size
 * @param {File} file - The file to analyze
 * @returns {Object} - Recommended compression options
 */
export const getOptimalCompressionSettings = (file) => {
  const fileSizeKB = file.size / 1024;
  
  // Default settings
  let settings = {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.8,
    maxSizeKB: 500,
    format: 'image/jpeg'
  };

  // Adjust based on file size
  if (fileSizeKB > 2000) { // > 2MB
    settings.maxWidth = 1600;
    settings.maxHeight = 900;
    settings.quality = 0.7;
    settings.maxSizeKB = 400;
  } else if (fileSizeKB > 1000) { // > 1MB
    settings.maxWidth = 1800;
    settings.maxHeight = 1000;
    settings.quality = 0.75;
    settings.maxSizeKB = 450;
  }

  // Preserve PNG format for images that might need transparency
  if (file.type === 'image/png' && fileSizeKB < 1000) {
    settings.format = 'image/png';
    settings.quality = 0.9;
  }

  return settings;
};

/**
 * Check if a file needs compression
 * @param {File} file - The file to check
 * @param {number} maxSizeKB - Maximum acceptable size in KB
 * @returns {boolean} - Whether the file needs compression
 */
export const needsCompression = (file, maxSizeKB = 500) => {
  return file.type.startsWith('image/') && file.size > maxSizeKB * 1024;
};